/**
 * Charts.
 *
 * Built from `View` primitives rather than a charting library, for three
 * reasons: a charting dependency would need a platform fork for native, it
 * would not honour the theme tokens, and the two shapes the dashboard actually
 * needs — a time series of bars and a ranked list — are about forty lines each.
 *
 * `DonutChart` is the exception, and the only one: a ring is arcs, and arcs are
 * the one thing a `View` cannot be made into. It is drawn with `react-native-svg`
 * — already a dependency, and already what the verification mark is drawn with —
 * as circles with a dash pattern, which needs no arc arithmetic and no library
 * beyond the one we have. Everything else here stays `View`-based.
 *
 * Labels, legends, axes, and empty states are `View` and `Text` with
 * `className`. Bar heights and widths stay in inline styles because they are
 * measured values no class can express, and their colours come from the
 * JavaScript palette for the same reason; see `lib/theme.ts`.
 */

import { View, type StyleProp, type ViewStyle } from 'react-native';
import Svg, { Circle } from 'react-native-svg';

import { Text } from './ui/text';
import { useAppearance } from '../providers/appearance';
import { niceScale } from '../lib/scale';
import { toneColors, type Tone } from '../lib/theme';

/** One bar in a series: a label, its primary value, and an optional second series. */
export interface BarDatum {
  label: string;
  value: number;
  /** Secondary series, drawn as a narrower bar in front. */
  secondary?: number;
}

/** Props for {@link BarChart}: the series and how to present it. */
export interface BarChartProps {
  data: BarDatum[];
  height?: number;
  tone?: Tone;
  secondaryTone?: Tone;
  /** Formats the figures on the value axis, and the peak figure under the bars. */
  formatValue?: (value: number) => string;
  /** Shows every nth label; the rest are suppressed to avoid collisions. */
  labelEvery?: number;
  emptyMessage?: string;
  /**
   * Draws a value axis down the left: round numbers and the rules between them.
   *
   * The bars are measured against the axis's top rather than against the tallest
   * of them, so "S$300" is the same height in every window and in every card, and
   * a dense series — a month of days, a year of months — can still be read, which
   * a number printed on each bar cannot do once the bars are narrower than the
   * number. The peak line steps aside for it: the axis is that scale, in full.
   */
  yAxis?: boolean;
  /**
   * The legend line's words, in the caller's own language.
   *
   * Passed in rather than written here. The line used to read "Bars: orders ·
   * inner: revenue" in English, with the outer bar's unit recovered from
   * `formatValue(0)` by stripping its digits — a number standing in for a word,
   * and one no translation could reach. A caller that passes nothing gets no
   * legend line, which is the honest default for a chart whose bars are already
   * named by the card around it.
   */
  legend?: {
    /** Names the full-height bar, where the chart draws a second series to tell it from. */
    primary?: string;
    /** Names the bar drawn inside it, for example "Net sales". */
    secondary?: string;
    /** Labels the tallest value on the axis, for example "Peak". */
    peak?: string;
  };
  style?: StyleProp<ViewStyle>;
}

/**
 * A vertical bar chart over a time series.
 *
 * Nothing is drawn behind a bar. Every bucket used to carry a track at full
 * height, so an empty day read as "no orders" rather than as missing data — but a
 * full-height grey column is the shape of a bar, so a quiet week looked like a
 * week of grey takings and a bar looked like a bar sitting in a taller one. The
 * axis and the day's own label say a bucket is there, and the rules across the
 * plot are the scale it is read against.
 *
 * @param props Series data and presentation options.
 * @returns A themed bar chart.
 */
export function BarChart({
  data,
  height = 180,
  tone = 'brand',
  secondaryTone = 'info',
  formatValue,
  labelEvery,
  emptyMessage = 'No data for this period',
  yAxis = false,
  legend,
  style,
}: BarChartProps) {
  const { palette } = useAppearance();

  if (data.length === 0) {
    return (
      <View className="items-center justify-center" style={[{ height }, style]}>
        <Text className="text-subtle-foreground text-xs">{emptyMessage}</Text>
      </View>
    );
  }

  // The floor of one is the scale's, not the data's: an all-zero series still
  // needs a denominator, but nothing was collected, and reporting the floor as
  // the peak prints "S$0.01" on a day with no sales at all.
  const values = data.map((d) => Math.max(d.value, d.secondary ?? 0));
  const max = Math.max(1, ...values);
  const peak = Math.max(0, ...values);

  // With an axis, the bars are measured against its round top instead of against
  // the tallest bar, which is what lets a reader compare a bar with a number
  // rather than only with its neighbours.
  const scale = yAxis ? niceScale(peak) : null;
  const ceiling = scale?.max ?? max;

  const primary = toneColors(palette, tone).solid;
  const secondaryColor = toneColors(palette, secondaryTone).solid;
  const every = labelEvery ?? Math.max(1, Math.ceil(data.length / 8));

  // The peak line is the scale in words, for a chart that has no axis to write it
  // down. With one, the same figures are already down the side.
  const seriesNamed = Boolean(legend?.secondary) && data.some((d) => d.secondary !== undefined);
  const peakStated = Boolean(legend?.peak) && !scale;

  // The gutters narrow as the buckets multiply. At eight points a year of days is
  // 267 buckets and more than two thousand points of gap, which is wider than the
  // card: every column was squeezed to nothing and the chart drew a heading, an
  // empty plot, and no labels at all. The four points and then the single point
  // keep the bars visible at any length, which is the whole job of a bar.
  const gutter = data.length > 120 ? 'gap-px' : data.length > 40 ? 'gap-1' : 'gap-2';

  // What the axis will print, once, so the numbers are not formatted twice and the
  // gutter can be sized from the same words it will show.
  const tickLabels = scale?.ticks.map((tick) => (formatValue ? formatValue(tick) : `${tick}`)) ?? [];

  return (
    <View className="gap-2" style={style}>
      <View className="flex-row gap-2">
        {scale ? (
          /* The axis's own words. Each sits on its rule rather than above it, so
             the number and the line it names are the same height on the page;
             `leading-4` fixes the line box that the half-line shift is half of.

             The column is as wide as its widest number and no wider. It was a fixed
             gutter, which left the longest number floating in the middle of it: the
             card's left margin to the first digit measured 46 points while the
             chart's right edge was 17 from the other side, and the two are supposed
             to read as one frame.

             Every number is laid out in the flow once, invisibly, which is what
             sizes the column — not a guess at which one is widest: "1k" is narrower
             than "4k", so counting characters put the year's axis two points out.
             The copy is `aria-hidden` and out of the pointer's way; it is a
             measuring stick, not a second reading. */
          <View className="relative shrink-0" style={{ height }}>
            <View aria-hidden className="pointer-events-none opacity-0">
              {tickLabels.map((label) => (
                <Text key={label} className="text-[11px] leading-4">
                  {label}
                </Text>
              ))}
            </View>
            {scale.ticks.map((tick, index) => (
              <Text
                key={tick}
                className="text-subtle-foreground absolute right-0 text-[11px] leading-4"
                style={{
                  bottom: `${(tick / scale.max) * 100}%`,
                  transform: [{ translateY: 8 }],
                }}>
                {tickLabels[index]}
              </Text>
            ))}
          </View>
        ) : null}

        <View className="flex-1">
          <View className="relative" style={{ height }}>
            {/* Two layers over one box: the rules, then the bars over them. The
                rules are the scale a bar is read against, so they have to be
                behind the bar that is being read — and nothing else is: a grey
                column at full height is the shape of a bar whatever it is called. */}
            {scale ? (
              <View className="absolute inset-0" pointerEvents="none">
                {scale.ticks.map((tick) => (
                  <View
                    key={`rule-${tick}`}
                    className="bg-border absolute right-0 left-0"
                    style={{ bottom: `${(tick / scale.max) * 100}%`, height: 1 }}
                  />
                ))}
              </View>
            ) : null}

            <View className={`absolute inset-0 flex-row items-end ${gutter}`}>
              {data.map((datum, index) => {
                const primaryPercent = (datum.value / ceiling) * 100;
                const secondaryPercent =
                  datum.secondary !== undefined ? (datum.secondary / ceiling) * 100 : null;

                return (
                  <View key={`${datum.label}-${index}`} className="h-full flex-1 justify-end">
                    {/* No bar either, at nothing: a fill of zero height is invisible
                        anyway, and leaving it out is one less view per quiet day in
                        a year of them. */}
                    {datum.value > 0 ? (
                      <View
                        className="w-full rounded"
                        style={{
                          height: `${Math.max(primaryPercent, 2)}%`,
                          backgroundColor: primary,
                        }}>
                        {secondaryPercent !== null ? (
                          <View
                            className="absolute bottom-0 left-[30%] right-[30%] rounded"
                            style={{
                              height: `${Math.max(secondaryPercent, (datum.secondary ?? 0) > 0 ? 2 : 0)}%`,
                              backgroundColor: secondaryColor,
                              opacity: 0.85,
                            }}
                          />
                        ) : null}
                      </View>
                    ) : null}
                  </View>
                );
              })}
            </View>
          </View>

          <View className={`mt-2 flex-row ${gutter}`}>
            {data.map((datum, index) => (
              <View key={`label-${datum.label}-${index}`} className="flex-1 items-center">
                {/* Nowrap rather than clamped: a cell is as narrow as its bar, so a
                    clamped label is an ellipsis, and the labels that survive
                    `labelEvery` are far enough apart to spill into the empty cells
                    beside them. */}
                <Text className="text-subtle-foreground text-[11px] whitespace-nowrap">
                  {index % every === 0 ? datum.label : ''}
                </Text>
              </View>
            ))}
          </View>
        </View>
      </View>

      {/* The peak is the scale in words, and a chart with an axis has already
          said it in numbers down the side. The row goes with the words that are
          left, so a chart with nothing to say underneath ends at its bars. */}
      {legend && (peakStated || seriesNamed) ? (
        <View className="flex-row justify-between gap-3">
          <Text className="text-subtle-foreground text-[11px]">
            {peakStated ? `${legend.peak} ${formatValue ? formatValue(peak) : peak}` : ''}
          </Text>
          {seriesNamed ? (
            <Text className="text-subtle-foreground text-[11px]">
              {legend.primary ? `${legend.primary} · ${legend.secondary}` : legend.secondary}
            </Text>
          ) : null}
        </View>
      ) : null}
    </View>
  );
}

/** One row in a ranked list: a label, its value, and an optional annotation. */
export interface RankedItem {
  label: string;
  value: number;
  /** Optional right-hand annotation, for example revenue. */
  note?: string;
}

/**
 * A ranked horizontal bar list.
 *
 * Preferred over a pie chart for "top items": comparing bar lengths is
 * accurate, comparing wedge angles is not.
 *
 * The bar is ten points thick, which is the thickness the dashboard's own rows
 * use: a bar that says the same kind of thing at a different weight reads as a
 * different kind of thing.
 *
 * @param props Items and formatting.
 * @returns A ranked list of labelled bars.
 */
export function RankedBars({
  items,
  tone = 'brand',
  formatValue,
  emptyMessage = 'Nothing sold in this period',
}: {
  items: RankedItem[];
  tone?: Tone;
  formatValue?: (value: number) => string;
  emptyMessage?: string;
}) {
  const { palette } = useAppearance();

  if (items.length === 0) {
    return <Text className="text-subtle-foreground text-xs">{emptyMessage}</Text>;
  }

  const max = Math.max(1, ...items.map((i) => i.value));
  const color = toneColors(palette, tone).solid;

  return (
    <View className="gap-3">
      {items.map((item, index) => (
        <View key={`${item.label}-${index}`} className="gap-1">
          <View className="flex-row items-center justify-between gap-3">
            <Text numberOfLines={1} className="flex-1 text-base">
              {item.label}
            </Text>
            <View className="flex-row items-center gap-2">
              {item.note ? (
                <Text className="text-subtle-foreground text-xs">{item.note}</Text>
              ) : null}
              <Text className="text-base font-bold">
                {formatValue ? formatValue(item.value) : item.value}
              </Text>
            </View>
          </View>
          <View className="bg-muted h-2.5 overflow-hidden rounded-full">
            <View
              className="h-full rounded-full"
              style={{ width: `${(item.value / max) * 100}%`, backgroundColor: color }}
            />
          </View>
        </View>
      ))}
    </View>
  );
}

/**
 * An hourly activity strip.
 *
 * Renders all 24 hours so the shape of the trading day is visible, rather than
 * only the hours that had orders.
 *
 * @param props Hourly counts and a tone.
 * @returns A 24-column strip with six-hour labels.
 */
export function HourStrip({
  hours,
  tone = 'brand',
  height = 72,
}: {
  hours: Array<{ hour: number; value: number }>;
  tone?: Tone;
  /**
   * The bar area's height in points, below the hour labels.
   *
   * A prop because it decides how much of a card the strip is worth: at the 72
   * it was written with, it read as a squashed footnote beside a ring twice its
   * height in the same card.
   */
  height?: number;
}) {
  const { palette } = useAppearance();
  const max = Math.max(1, ...hours.map((h) => h.value));
  const color = toneColors(palette, tone).solid;

  const byHour = new Map(hours.map((h) => [h.hour, h.value]));

  return (
    <View className="gap-2">
      <View className="flex-row items-end gap-0.5" style={{ height }}>
        {Array.from({ length: 24 }, (_, hour) => {
          const value = byHour.get(hour) ?? 0;
          return (
            <View key={hour} className="h-full flex-1 justify-end">
              <View
                style={{
                  height: `${Math.max((value / max) * 100, value > 0 ? 4 : 1.5)}%`,
                  backgroundColor: value > 0 ? color : palette.border,
                  borderRadius: 2,
                }}
              />
            </View>
          );
        })}
      </View>
      <View className="flex-row gap-0.5">
        {Array.from({ length: 24 }, (_, hour) => (
          <View key={hour} className="flex-1 items-center">
            <Text className="text-subtle-foreground text-[11px]">
              {hour % 6 === 0 ? String(hour).padStart(2, '0') : ''}
            </Text>
          </View>
        ))}
      </View>
    </View>
  );
}

/** One slice of a {@link DonutChart}: what it is, how much of the whole it is. */
export interface DonutSegment {
  label: string;
  value: number;
  tone?: Tone;
  /**
   * An explicit colour, where a tone would say something the split does not.
   *
   * A tone is a judgement — success, warning, info — and some splits have no
   * judgement to make: booked against walk-up is two kinds of order, not a good
   * one and a bad one. Those pass two greys by hand instead, which is also the
   * only way to get two of them, since `neutral` is one colour.
   */
  color?: string;
}

/**
 * A ring split into segments, with the leader in the middle.
 *
 * For a part-of-a-whole that a progress bar states badly: a bar says "this much
 * so far", a ring says "this much of the whole", and the hole is somewhere to put
 * the number a reader wants first.
 *
 * Drawn as one circle per segment with a dash pattern long enough to cover its
 * share and a gap for the rest, offset by the shares before it — which is why it
 * needs no trigonometry. The whole thing is decorative: the legend under it
 * carries the same figures as text, so a screen reader loses nothing by being
 * told to skip the drawing.
 *
 * The legend sits under the ring rather than beside it, which is what lets the
 * ring be large: sharing a row, the ring was only ever as wide as the card minus
 * the words, and a ring is read by its arcs. Underneath, it takes the full width
 * of its column and the words take the line they need.
 *
 * @param props The segments, the size and thickness, and what the hole says.
 * @returns The ring.
 */
export function DonutChart({
  segments,
  size = 150,
  thickness = 26,
  centerLabel,
  centerHint,
}: {
  segments: DonutSegment[];
  /**
   * Outer diameter in points.
   *
   * Big enough that the hole holds the centre figure at a readable size: at 150
   * across and 26 thick the hole is 98 points, which is a percentage and its
   * caption with room around them. The legend is under the ring rather than
   * beside it for the same reason — sharing a row, the ring was only ever as
   * wide as the card minus the words.
   */
  size?: number;
  /**
   * How thick the band is, in points.
   *
   * Thick by default: a ring is read by its arcs, and at a sixteenth of its own
   * diameter the band is a line rather than a shape. The hole it leaves is what
   * the centre figure has to fit in — at 150 across and 26 thick that is 98
   * points, which holds the percentage and its caption.
   */
  thickness?: number;
  centerLabel?: string;
  centerHint?: string;
}) {
  const { palette } = useAppearance();
  const total = segments.reduce((sum, segment) => sum + Math.max(0, segment.value), 0);
  const radius = (size - thickness) / 2;
  const circumference = 2 * Math.PI * radius;

  let travelled = 0;
  const drawn = segments
    .filter((segment) => segment.value > 0)
    .map((segment) => {
      const share = total > 0 ? segment.value / total : 0;
      const arc = {
        key: segment.label,
        // A hair of overlap, so two segments that meet do not show the track
        // through the seam at this thickness.
        dash: `${Math.max(share * circumference - 1, 0.5)} ${circumference}`,
        offset: -travelled * circumference,
        color: segment.color ?? toneColors(palette, segment.tone ?? 'neutral').solid,
      };
      travelled += share;
      return arc;
    });

  return (
    <View className="flex-col items-center gap-4">
      <View style={{ width: size, height: size }} aria-hidden>
        <Svg width={size} height={size}>
          <Circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            stroke={palette.muted}
            strokeWidth={thickness}
            fill="none"
          />
          {drawn.map((arc) => (
            <Circle
              key={arc.key}
              cx={size / 2}
              cy={size / 2}
              r={radius}
              stroke={arc.color}
              strokeWidth={thickness}
              fill="none"
              strokeDasharray={arc.dash}
              strokeDashoffset={arc.offset}
              // Start at twelve o'clock rather than at three.
              transform={`rotate(-90 ${size / 2} ${size / 2})`}
            />
          ))}
        </Svg>
        {centerLabel ? (
          <View className="absolute inset-0 items-center justify-center">
            <Text className="text-xl font-bold">{centerLabel}</Text>
            {centerHint ? (
              <Text className="text-subtle-foreground text-[11px]">{centerHint}</Text>
            ) : null}
          </View>
        ) : null}
      </View>

      {/* Wrapping, so a third segment or a longer label in Chinese folds onto a
          second line instead of squeezing the ring it sits under. */}
      <View className="flex-row flex-wrap items-center justify-center gap-x-6 gap-y-2">
        {segments.map((segment) => (
          <View key={segment.label} className="flex-row items-center gap-2">
            <View
              style={{
                width: 10,
                height: 10,
                borderRadius: 999,
                backgroundColor: segment.color ?? toneColors(palette, segment.tone ?? 'neutral').solid,
              }}
            />
            <Text className="text-muted-foreground text-sm">{segment.label}</Text>
            <Text className="text-base font-bold">{segment.value}</Text>
          </View>
        ))}
      </View>
    </View>
  );
}
